import { ArrowRight, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react"; // Added for animation state
import serviceovervewbg from "../../Images/services-overview-HomePg.webp";

const ServicesOverview = () => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false); // Added state for animation

  const services = [
    {
      id: 1,
      title: "Market Entry & Advisory",
      description:
        "We provide strategic, operational, and financial consulting for businesses navigating the complexities of the Saudi and GCC economies.",
    },
    {
      id: 2,
      title: "Legal & Compliance",
      description:
        "We offer comprehensive legal and regulatory advisory services to ensure businesses comply with Saudi and GCC laws and regulations.",
    },
    {
      id: 3,
      title: "Investment Opportunities",
      description:
        "We identify and evaluate investment opportunities in Saudi Arabia and the GCC region, offering tailored strategies for growth and success.",
    },
    {
      id: 4,
      title: "Leadership Team",
      description:
        "Our leadership team brings decades of experience in the Saudi and GCC markets, ensuring businesses have the best possible guidance and support.",
    },
    {
      id: 5,
      title: "Partnership Services",
      description:
        "We help businesses build strategic partnerships that drive mutual success and sustainable growth in the Saudi and GCC markets.",
    },
  ];

  // Enhanced navigation function with animation and scroll to top
  const handleLearnMoreClick = () => {
    setIsNavigating(true);
    
    // Add a slight delay for visual feedback
    setTimeout(() => {
      navigate('/contact');
      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }, 300);
  };

  return (
    <section className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${serviceovervewbg})`,
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        {/* Main Heading */}
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-2xl md:text-4xl lg:text-6xl font-bold text-white mb-4">
            Services Overview
          </h2>
        </div>

        {/* Mobile Layout */}
        <div className="block md:hidden space-y-6 mb-12">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-black/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <Settings className="w-6 h-6 text-[#C99C48]" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Layout with Structured Grid */}
        <div className="hidden md:block mb-16 px-4">
          {/* First row with 3 cards */}
          <div className="grid grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <div
                key={service.id}
                className="bg-black/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:bg-black/80 transition-all duration-300 hover:scale-105 w-64 lg:w-72 mx-auto"
                data-aos="fade-up"
                data-aos-duration="500"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-[#C99C48]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Second row with 2 cards, spaced around */}
          <div className="flex justify-around mt-10">
            {services.slice(3, 5).map((service) => (
              <div
                key={service.id}
                className="  w-64 lg:w-72 bg-black/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:bg-black/80 transition-all duration-300 hover:scale-105"
                data-aos="fade-up"
                data-aos-duration="600"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-[#C99C48]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Learn More Button - Enhanced with animation */}
        <div className="text-center mt-12 md:mt-0">
          <button 
            onClick={handleLearnMoreClick}
            disabled={isNavigating}
            className={`inline-flex items-center space-x-2 bg-[#C99C48] hover:bg-[#B8914A] text-black font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95 ${
              isNavigating ? 'opacity-70 scale-95 cursor-not-allowed' : ''
            }`}
          >
            <span>{isNavigating ? 'Navigating...' : 'Learn More'}</span>
            <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isNavigating ? 'translate-x-1' : ''}`} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;